import type { Table as TanstackTable } from '@tanstack/react-table';
import { px } from '@this/app-shared/dist/tools/styles.js';
import { type RefObject, useMemo } from 'react';

export function useResizableTableContext<TData>({
    table,
    padContainerValue,
    maxWidth,
    hasInitialized,
    containerRef,
    tableRef,
    columnSizes,
    columnOrder,
    columnVisibility,
    rowSelection,
    rowSelectionHighlights,
}: {
    table: TanstackTable<TData>;
    padContainerValue?: number | string;
    maxWidth?: number;
    hasInitialized: boolean;
    containerRef: RefObject<HTMLDivElement>;
    tableRef: RefObject<HTMLTableElement>;
    rowSelection: Record<string, boolean>;
    rowSelectionHighlights?: string[];
    columnSizes: Record<string, number>;
    columnOrder?: string[];
    columnVisibility?: Record<string, boolean>;
}) {
    const { rows } = table.getRowModel();

    return useMemo(
        () => ({
            hasInitialized,
            padContainerValue,
            maxWidth: maxWidth ? px(maxWidth) : undefined,
            containerRef,
            tableRef,
            rows,
            rowSelection,
            rowSelectionHighlights,
            columnOrder,
            columnSizes,
            columnVisibility,
        }),
        [
            hasInitialized,
            padContainerValue,
            maxWidth,
            containerRef,
            tableRef,
            rows,
            rowSelection,
            rowSelectionHighlights,
            columnOrder,
            columnSizes,
            columnVisibility,
        ]
    );
}

export type ContextModel<TData> = ReturnType<typeof useResizableTableContext<TData>>;
